import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { citiesAPI, imageAPI } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';
import { Pencil, Trash2, Plus, Upload, X } from 'lucide-react';

const EMPTY = {
  slug: '', name: '', profile_image: '', intro: '',
  landmarks: [], how_to_reach: '', how_ecoway_helps: '',
  key_details: [], seo_title: '', seo_description: '', seo_keywords: '', published: true,
};

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const CitiesManager = () => {
  const { toast } = useToast();
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try { const res = await citiesAPI.adminGetAll(); setCities(res.data); } catch (e) { console.error('Failed to load cities', e); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(EMPTY); setEditingId(null); setShowForm(false); };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      slug: c.slug, name: c.name, profile_image: c.profile_image || '', intro: c.intro || '',
      landmarks: (c.landmarks || []).map((l) => ({ ...l, _uid: Math.random() })),
      how_to_reach: c.how_to_reach || '', how_ecoway_helps: c.how_ecoway_helps || '',
      key_details: (c.key_details || []).map((d) => ({ ...d, _uid: Math.random() })), seo_title: c.seo_title || '', seo_description: c.seo_description || '',
      seo_keywords: c.seo_keywords || '', published: c.published !== false,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startAdd = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };

  const uploadImage = async (file, cb) => {
    setUploading(true);
    try { const res = await imageAPI.upload(file); cb(res.data.url); toast({ title: 'Success', description: 'Image uploaded' }); }
    catch (err) { toast({ title: 'Error', description: err.response?.data?.detail || 'Upload failed', variant: 'destructive' }); }
    finally { setUploading(false); }
  };

  // landmark helpers
  const addLandmark = () => setForm((f) => ({ ...f, landmarks: [...f.landmarks, { name: '', description: '', image: '', _uid: Math.random() }] }));
  const updateLandmark = (i, field, v) => setForm((f) => ({ ...f, landmarks: f.landmarks.map((l, idx) => idx === i ? { ...l, [field]: v } : l) }));
  const removeLandmark = (i) => setForm((f) => ({ ...f, landmarks: f.landmarks.filter((_, idx) => idx !== i) }));

  // key detail helpers
  const addDetail = () => setForm((f) => ({ ...f, key_details: [...f.key_details, { label: '', value: '', _uid: Math.random() }] }));
  const updateDetail = (i, field, v) => setForm((f) => ({ ...f, key_details: f.key_details.map((d, idx) => idx === i ? { ...d, [field]: v } : d) }));
  const removeDetail = (i) => setForm((f) => ({ ...f, key_details: f.key_details.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      slug: slugify(form.slug || form.name),
      landmarks: form.landmarks.map(({ _uid, ...l }) => l),
      key_details: form.key_details.map(({ _uid, ...d }) => d),
    };
    try {
      if (editingId) { await citiesAPI.update(editingId, payload); toast({ title: 'Success', description: 'City updated' }); }
      else { await citiesAPI.create(payload); toast({ title: 'Success', description: 'City added' }); }
      resetForm(); load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save city', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this city page?')) return;
    try { await citiesAPI.remove(id); toast({ title: 'Deleted', description: 'City removed' }); if (editingId === id) resetForm(); load(); }
    catch (err) { toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' }); }
  };

  return (
    <div className="space-y-6">
      {!showForm && (
        <Button onClick={startAdd} data-testid="city-add-button"><Plus className="h-4 w-4 mr-1" />Add City</Button>
      )}

      {showForm && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit City' : 'Add New City'}</h3>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="city-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>City Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="city-name-input" />
                </div>
                <div>
                  <Label>Slug (URL) <span className="text-xs text-gray-400">/cities/...</span></Label>
                  <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto from name if blank" data-testid="city-slug-input" />
                </div>
              </div>

              <div>
                <Label>Profile Image</Label>
                <div className="flex items-center gap-3 mb-2">
                  {form.profile_image && <img src={form.profile_image} alt="preview" className="h-16 w-24 object-cover rounded border" data-testid="city-image-preview" />}
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center px-3 py-2 bg-gray-900 text-white rounded-md text-sm hover:bg-gray-800"><Upload className="h-4 w-4 mr-2" />{uploading ? 'Uploading...' : 'Upload'}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], (url) => setForm((f) => ({ ...f, profile_image: url })))} data-testid="city-image-upload" />
                  </label>
                </div>
                <Input value={form.profile_image} onChange={(e) => setForm({ ...form, profile_image: e.target.value })} placeholder="https://... or upload above" data-testid="city-image-input" />
              </div>

              <div>
                <Label>Intro / About</Label>
                <Textarea rows={3} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} data-testid="city-intro-input" />
              </div>

              {/* Landmarks */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label>Key Landmarks</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addLandmark} data-testid="city-add-landmark"><Plus className="h-4 w-4 mr-1" />Add Landmark</Button>
                </div>
                <div className="space-y-3">
                  {form.landmarks.map((lm, i) => (
                    <div key={lm._uid || i} className="border rounded-lg p-3 space-y-2 relative" data-testid={`landmark-row-${i}`}>
                      <button type="button" onClick={() => removeLandmark(i)} className="absolute top-2 right-2 text-gray-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                      <Input placeholder="Landmark name" value={lm.name} onChange={(e) => updateLandmark(i, 'name', e.target.value)} data-testid={`landmark-name-${i}`} />
                      <Textarea rows={2} placeholder="Description" value={lm.description} onChange={(e) => updateLandmark(i, 'description', e.target.value)} />
                      <div className="flex items-center gap-2">
                        {lm.image && <img src={lm.image} alt="" className="h-10 w-10 object-cover rounded" />}
                        <label className="cursor-pointer text-sm text-blue-700">
                          <span className="underline">Upload image</span>
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], (url) => updateLandmark(i, 'image', url))} />
                        </label>
                        <Input placeholder="or image URL" value={lm.image} onChange={(e) => updateLandmark(i, 'image', e.target.value)} className="flex-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>How to Get There from Ahmedabad</Label>
                <Textarea rows={2} value={form.how_to_reach} onChange={(e) => setForm({ ...form, how_to_reach: e.target.value })} data-testid="city-reach-input" />
              </div>

              <div>
                <Label>How Ecoway Cabs Can Help</Label>
                <Textarea rows={2} value={form.how_ecoway_helps} onChange={(e) => setForm({ ...form, how_ecoway_helps: e.target.value })} data-testid="city-help-input" />
              </div>

              {/* Key details */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label>Key Details (label / value)</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addDetail} data-testid="city-add-detail"><Plus className="h-4 w-4 mr-1" />Add Detail</Button>
                </div>
                <div className="space-y-2">
                  {form.key_details.map((kd, i) => (
                    <div key={kd._uid || i} className="flex gap-2 items-center" data-testid={`detail-row-${i}`}>
                      <Input placeholder="Label (e.g. Distance)" value={kd.label} onChange={(e) => updateDetail(i, 'label', e.target.value)} />
                      <Input placeholder="Value (e.g. ~440 km)" value={kd.value} onChange={(e) => updateDetail(i, 'value', e.target.value)} />
                      <button type="button" onClick={() => removeDetail(i)} className="text-gray-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* SEO */}
              <div className="border-t pt-4 space-y-3">
                <Label className="text-base font-semibold">SEO</Label>
                <div>
                  <Label>Meta Title</Label>
                  <Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} data-testid="city-seo-title-input" />
                </div>
                <div>
                  <Label>Meta Description</Label>
                  <Textarea rows={2} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} data-testid="city-seo-desc-input" />
                </div>
                <div>
                  <Label>Meta Keywords</Label>
                  <Input value={form.seo_keywords} onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })} data-testid="city-seo-keywords-input" />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="city-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4" data-testid="city-published-checkbox" />
                <Label htmlFor="city-published" className="text-sm">Published (visible on site)</Label>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={saving} data-testid="city-save-button">{saving ? 'Saving...' : (editingId ? 'Update City' : 'Add City')}</Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {cities.map((c) => (
          <div key={c.id} className="border rounded-lg p-4 flex items-center justify-between bg-white" data-testid={`city-row-${c.slug}`}>
            <div className="flex items-center gap-3">
              {c.profile_image && <img src={c.profile_image} alt={c.name} className="h-14 w-20 object-cover rounded" />}
              <div>
                <h4 className="font-semibold">{c.name} {!c.published && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Draft</span>}</h4>
                <p className="text-xs text-gray-500">/cities/{c.slug} • {(c.landmarks || []).length} landmarks</p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0 ml-2">
              <Button size="sm" variant="outline" onClick={() => startEdit(c)} data-testid={`city-edit-${c.slug}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(c.id)} data-testid={`city-delete-${c.slug}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CitiesManager;
